import { useState, useEffect, useImperativeHandle, Ref } from 'react';
import OptionsRangeFilter from '@/components/filters/OptionsRangeFilter/OptionsRangeFilter';
import { Resettable } from '@/models/Resettable';
import { getCurrentDate, getRelativeDate, getFirstDayOfMonth, getFirstDayOfYear, getPreviousMonthRange } from '@/helpers/dateHelper';

interface DateOption {
  id: string;
  label: string;
  checked: boolean;
  startDate: string;
  endDate: string;
}

interface DateFilterProps {
  onDateRangeChange: (from?: string, to?: string) => void;
  ref?: Ref<Resettable>;
  dateRange?: { start?: string; end?: string };
  /**
   * HAT-1212: העמוד עדיין מרענן — חצי הפילטר מקבלים disabled ויזואלי.
   * מסופק ע"י TransactionPage מתוך `isLoading` של useGetAuthApi.
   */
  isBusy?: boolean;
}

const DateFilter = ({ onDateRangeChange, dateRange, ref, isBusy }: DateFilterProps) => {
  const previousMonth = getPreviousMonthRange();

  const [options, setOptions] = useState<DateOption[]>([
    {
      id: 'last3Days',
      label: 'last3Days',
      checked: false,
      startDate: getRelativeDate(-2),
      endDate: getCurrentDate(),
    },
    {
      id: 'last7Days',
      label: 'last7Days',
      checked: false,
      startDate: getRelativeDate(-6),
      endDate: getCurrentDate(),
    },
    {
      id: 'sinceStartOfMonth',
      label: 'sinceStartOfMonth',
      checked: false,
      startDate: getFirstDayOfMonth(),
      endDate: getCurrentDate(),
    },
    {
      id: 'previousMonth',
      label: 'previousMonth',
      checked: false,
      startDate: previousMonth.start,
      endDate: previousMonth.end,
    },
    {
      id: 'last30Days',
      label: 'last30Days',
      checked: false,
      startDate: getRelativeDate(-29),
      endDate: getCurrentDate(),
    },
    {
      id: 'last90Days',
      label: 'last90Days',
      checked: true,
      startDate: getRelativeDate(-89),
      endDate: getCurrentDate(),
    },
    {
      id: 'sinceStartOfYear',
      label: 'sinceStartOfYear',
      checked: false,
      startDate: getFirstDayOfYear(),
      endDate: getCurrentDate(),
    },
  ]);

  const [fromValue, setFromValue] = useState<string>('');
  const [toValue, setToValue] = useState<string>('');
  const [appliedRange, setAppliedRange] = useState<{ from: string; to: string } | null>(null);

  const start = dateRange?.start;
  const end = dateRange?.end;
  useEffect(() => {
    if (!start || !end) return;
    setOptions((previous) => previous.map((option) => ({ ...option, checked: option.startDate === start && option.endDate === end })));
    const preset = options.some((option) => option.startDate === start && option.endDate === end);
    setAppliedRange(preset ? null : { from: start, to: end });
    setFromValue(preset ? '' : start);
    setToValue(preset ? '' : end);
    // Preset date boundaries do not change during the mounted filter session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end]);

  const handleOptionChange = (optionId: string) => {
    const updatedOptions = options.map((option) => ({ ...option, checked: option.id === optionId }));
    setOptions(updatedOptions);

    const selectedOption = updatedOptions.find((option) => option.checked);

    if (selectedOption) {
      onDateRangeChange(selectedOption.startDate, selectedOption.endDate);
    }
  };

  const handleRangeApply = (from: string, to: string) => {
    const updatedOptions = options.map((option) => ({ ...option, checked: false }));
    setOptions(updatedOptions);

    onDateRangeChange(from, to);
  };

  useImperativeHandle(ref, () => ({
    reset: () => {
      setOptions((prevOptions) =>
        prevOptions.map((option) => ({
          ...option,
          checked: option.id === 'last90Days',
        }))
      );
      setFromValue('');
      setToValue('');
      setAppliedRange(null);
    },
  }));

  return (
    <OptionsRangeFilter
      title="dateRange"
      filterType="options-and-range"
      options={options.map(({ id, label, checked }) => ({ id, label, checked }))}
      onOptionChange={handleOptionChange}
      selectionType="radio"
      rangeConfig={{
        type: 'date',
        fromPlaceholder: 'fromDate',
        toPlaceholder: 'toDate',
      }}
      hebrewCalendar
      fromValue={fromValue}
      setFromValue={setFromValue}
      toValue={toValue}
      setToValue={setToValue}
      appliedRange={appliedRange}
      setAppliedRange={setAppliedRange}
      onRangeApply={handleRangeApply}
      isBusy={isBusy}
    />
  );
};

export default DateFilter;
